const GAL = 231;
const sphereGal = (d) => ((4 / 3) * Math.PI * (d / 2) ** 3) / GAL;

export const headGallons = (d, ends) => (ends !== "flat" && d > 0 ? sphereGal(d) : 0);

const headWeight = (d, cap, ends) =>
  ends === "flat" || cap <= 0 ? 0 : Math.min(0.6, headGallons(d, ends) / cap);

export function cylinderVolumeFraction(h, d) {
  if (h <= 0 || d <= 0) return 0;
  if (h >= d) return 1;
  const x = h / d;
  return (Math.acos(1 - 2 * x) - (1 - 2 * x) * Math.sqrt(Math.max(0, 4 * x - 4 * x * x))) / Math.PI;
}

export function tankFraction(h, d, cap, ends = "flat") {
  if (h <= 0 || d <= 0) return 0;
  if (h >= d) return 1;
  const c = cylinderVolumeFraction(h, d);
  const w = headWeight(d, cap, ends);
  if (w === 0) return c;
  const x = h / d;
  return (1 - w) * c + w * (3 * x * x - 2 * x * x * x);
}

export function calculateGpi(h, d, cap, ends = "flat") {
  if (h <= 0 || h >= d || d <= 0) return 0;
  const r = d / 2;
  const chord = 2 * Math.sqrt(Math.max(0, 2 * r * h - h * h));
  const w = headWeight(d, cap, ends);
  const x = h / d;
  const sph = (6 * x - 6 * x * x) / d;
  return ((1 - w) * (chord / (Math.PI * r * r)) + w * sph) * cap;
}

export function calculateVolume(h, d, cap, ends = "flat") {
  if (h <= 0 || cap <= 0) return 0;
  if (h >= d) return Math.round(cap);
  return Math.round(tankFraction(h, d, cap, ends) * cap);
}

export function solveInchesFromVolume(gal, d, cap, ends = "flat") {
  if (gal <= 0 || d <= 0 || cap <= 0) return 0;
  if (gal >= cap) return d;
  const t = gal / cap;
  let lo = 0, hi = d;
  for (let i = 0; i < 60; i++) {
    const m = (lo + hi) / 2;
    if (tankFraction(m, d, cap, ends) < t) lo = m;
    else hi = m;
  }
  return (lo + hi) / 2;
}

export function solveDiameter(h, gal, cap, ends = "flat") {
  if (h <= 0 || gal <= 0 || cap <= 0 || gal >= cap) return null;
  const t = gal / cap;
  let lo = h, hi = 400;
  if (tankFraction(h, hi, cap, ends) > t) return null;
  for (let i = 0; i < 60; i++) {
    const m = (lo + hi) / 2;
    if (tankFraction(h, m, cap, ends) > t) lo = m;
    else hi = m;
  }
  return (lo + hi) / 2;
}

export const EIGHTHS = [0, 0.125, 0.25, 0.375, 0.5, 0.625, 0.75, 0.875];
const EIGHTH_LABELS = ["0", "⅛", "¼", "⅜", "½", "⅝", "¾", "⅞"];
export const eighthLabel = (i) => EIGHTH_LABELS[i] ?? "0";

export function formatToEighths(v) {
  if (!Number.isFinite(v)) return "—";
  let whole = Math.floor(v + 1e-8);
  let e = Math.round((v - whole) * 8);
  if (e >= 8) { whole += 1; e = 0; }
  if (e > 0 && whole === 0) return `${EIGHTH_LABELS[e]} in`;
  return e > 0 ? `${whole}-${EIGHTH_LABELS[e]} in` : `${whole} in`;
}

export const formatGal = (g) => Math.round(g).toLocaleString("en-US");
export const formatGpi = (g) => g.toFixed(1);
export const clamp = (v, min, max) => (Number.isFinite(v) ? Math.min(max, Math.max(min, v)) : min);
export const tankKey = (t) => `${t.capacity}-${t.diameter}-${t.ends}`;
// The last console scan still describes this stick on this tank.
export const scanActive = (s) => !!s.scan && Math.abs((s.scan.inches ?? -1) - s.startInches) < 0.01 && (!s.scan.key || s.scan.key === tankKey(s));
export const endsLabel = (e) => (e === "dome" ? "rounded ends" : "flat ends");
export const endsShort = (e) => (e === "dome" ? "FG" : "steel");

export function lengthInches(cap, d, ends = "flat") {
  if (cap <= 0 || d <= 0) return 0;
  const r = d / 2;
  const cyl = Math.max(0, cap - headGallons(d, ends));
  return (cyl * GAL) / (Math.PI * r * r) + (ends === "dome" ? d : 0);
}

export function impliedLengthLabel(cap, d, ends = "flat") {
  if (cap <= 0 || d <= 0) return "—";
  const len = Math.round(lengthInches(cap, d, ends));
  return `~${Math.floor(len / 12)} ft ${len % 12} in`;
}

// Horizontal cylinder (plus half-sphere ends when dished/rounded), end to end inside length.
export function capacityFromDims(d, len, ends = "flat") {
  if (!(d > 0) || !(len > 0)) return 0;
  const cylLen = ends === "dome" ? len - d : len;
  if (cylLen <= 0) return 0;
  return Math.round((Math.PI * (d / 2) ** 2 * cylLen) / GAL + headGallons(d, ends));
}

const FIBERGLASS = {
  48: [550, 600, 1000, 2000], 72: [2000, 3000, 4000, 5000, 6000, 8000, 10000],
  96: [4000, 5000, 6000, 8000, 10000, 12000, 15000, 20000],
  120: [10000, 12000, 15000, 20000, 25000, 30000, 35000, 40000],
  138: [20000, 25000, 30000, 35000, 40000, 48000, 50000, 60000],
};
const STEEL = {
  48: [550, 1000], 64: [1000, 2000, 3000, 4000], 72: [4000, 5000, 6000], 84: [6000, 8000, 10000],
  96: [8000, 10000, 12000, 15000, 20000], 108: [6000, 8000, 10000, 12000, 15000, 20000],
  120: [10000, 12000, 15000, 20000, 25000], 126: [12000, 15000, 20000, 25000, 30000, 32000, 34000, 36000],
  132: [15000, 20000, 25000, 30000], 144: [25000, 30000, 40000, 50000],
};

function buildDb() {
  const list = [];
  const add = (table, ends, mfg) => {
    for (const [dia, caps] of Object.entries(table)) {
      const d = Number(dia);
      for (const cap of caps) {
        if (ends === "dome" && cap * 0.6 < headGallons(d, ends)) continue;
        list.push({
          capacity: cap, diameter: d, ends,
          mfg: cap === 10000 && d === 96 && ends === "dome" ? `${mfg} · most stations` : mfg,
          lengthLabel: impliedLengthLabel(cap, d, ends),
          sti: d > 120 ? true : undefined,
        });
      }
    }
  };
  add(FIBERGLASS, "dome", "Fiberglass · Xerxes, Containment Solutions, ZCL, Owens Corning");
  add(STEEL, "flat", "Steel · Highland, Modern Welding, STI, Permatank");
  return list.sort((a, b) => a.capacity - b.capacity || a.diameter - b.diameter || a.ends.localeCompare(b.ends));
}

export const UST_DATABASE = buildDb();
const isPresetTank = (s) => UST_DATABASE.some((x) => x.capacity === s.capacity && x.diameter === s.diameter && x.ends === s.ends);
// Where the gallons-per-inch come from. A console start keeps the tank's own source.
export function chartSource(s, r) {
  if (r?.source === "chart") return "maker";
  const onSite = !!s.activeSiteId && s.activeKey === tankKey(s);
  if (onSite && s.activeEpa) return "epa";
  return s.customDims || !isPresetTank(s) ? "custom" : "generic";
}
export const TANK_PRESETS = UST_DATABASE;
export const CAPACITIES = Array.from(new Set(UST_DATABASE.map((t) => t.capacity)));
export const CAPACITY_FILTERS = ["ANY", ...Array.from(new Set(UST_DATABASE.map((t) => String(t.capacity))))];

export const PRODUCTS = [
  { id: "Regular 87", short: "Reg 87", name: "REGULAR UNLEADED 87", api: "API White", tone: "reg" },
  { id: "Midgrade 89", short: "Mid 89", name: "MIDGRADE UNLEADED 89", api: "API Blue", tone: "mid" },
  { id: "Premium 93", short: "Prem 93", name: "PREMIUM UNLEADED 93", api: "API Red", tone: "prem" },
  { id: "Diesel #2 (ULSD)", short: "Diesel #2", name: "ULTRA LOW SULFUR DIESEL #2", api: "API Yellow", tone: "dsl" },
  { id: "DEF", short: "DEF", name: "DIESEL EXHAUST FLUID (DEF)", api: "Blue (DEF)", tone: "def" },
  { id: "Kerosene", short: "Kero", name: "KEROSENE", api: "API Brown", tone: "kero" },
];
// API RP 1637 colors: regular white, midgrade blue, premium red, diesel yellow, kerosene brown.
const GRADE = { reg: "#F8FAFC", mid: "#2563EB", prem: "#DC2626", dsl: "#FACC15", def: "#38BDF8", kero: "#92400E" };
export const productName = (id) => PRODUCTS.find((p) => p.id === id)?.name || id;
export const productColor = (id) => GRADE[PRODUCTS.find((p) => p.id === id)?.tone] || "#F59E0B";
export const productShort = (id) => PRODUCTS.find((p) => p.id === id)?.short || id;

export const DROP_PRESETS = [1000, 1500, 2000, 2500, 3000, 3500, 4000, 5000, 8500];

export const findSpec = (cap, d, ends) =>
  UST_DATABASE.find((t) => t.capacity === cap && t.diameter === d && t.ends === ends);
export const lengthFor = (cap, d, ends) => findSpec(cap, d, ends)?.lengthLabel ?? impliedLengthLabel(cap, d, ends);

export function defaultEnds(cap, d) {
  const m = UST_DATABASE.filter((t) => t.capacity === cap && t.diameter === d);
  return m.length === 1 ? m[0].ends : "flat";
}

export function projectDrop(startInches, dropGallons, d, cap, ends = "flat", consoleGallons = null) {
  const startGallons = consoleGallons ?? calculateVolume(startInches, d, cap, ends);
  const startPct = cap > 0 ? (startGallons / cap) * 100 : 0;
  const startGpi = calculateGpi(startInches, d, cap, ends);
  const finalGallons = startGallons + Math.max(0, dropGallons);
  const finalInches = solveInchesFromVolume(finalGallons, d, cap, ends);
  const finalPct = cap > 0 ? (finalGallons / cap) * 100 : 0;
  const cap90 = Math.round(cap * 0.9);
  const cap95 = Math.round(cap * 0.95);
  const status = finalPct > 95 ? "critical" : finalPct > 90 ? "caution" : "safe";
  return {
    startGallons, startPct, startGpi, finalGallons, finalInches, finalPct,
    stickGain: finalInches - startInches, cap90, cap95,
    ullage90: cap90 - finalGallons, ullage95: cap95 - finalGallons,
    startUllage90: Math.max(0, cap90 - startGallons), status,
    h90: solveInchesFromVolume(cap90, d, cap, ends), h95: solveInchesFromVolume(cap95, d, cap, ends),
  };
}

const byDelta = (a, b) => a.delta - b.delta;

export function rankTanksByDrop(start, end, delivered) {
  if (end <= start || delivered <= 0) return [];
  return UST_DATABASE.filter((t) => t.diameter + 0.001 >= end)
    .map((t) => {
      const rise = calculateVolume(end, t.diameter, t.capacity, t.ends) - calculateVolume(start, t.diameter, t.capacity, t.ends);
      const delta = Math.abs(rise - delivered);
      return { ...t, riseGallons: rise, delta, deltaPct: (delta / delivered) * 100 };
    })
    .sort(byDelta);
}

export function rankTanks(h, gallons, capFilter) {
  if (h <= 0 || gallons <= 0) return [];
  let list = UST_DATABASE.filter((t) => t.diameter > h);
  if (capFilter !== "ANY") list = list.filter((t) => t.capacity === Number(capFilter));
  return list
    .map((t) => {
      const calcVol = calculateVolume(h, t.diameter, t.capacity, t.ends);
      const delta = Math.abs(calcVol - gallons);
      return { ...t, calcVol, delta, deltaPct: (delta / gallons) * 100, fillPct: tankFraction(h, t.diameter, t.capacity, t.ends) * 100 };
    })
    .sort(byDelta);
}

export function rankTanksByUllage(h, ullage, kind) {
  if (h <= 0 || ullage <= 0) return [];
  return UST_DATABASE.filter((t) => t.diameter > h)
    .map((t) => {
      const vol = calculateVolume(h, t.diameter, t.capacity, t.ends);
      const calcVol = (kind !== "90" ? t.capacity : t.capacity * 0.9) - vol;
      const delta = Math.abs(calcVol - ullage);
      return { ...t, calcVol, delta, deltaPct: (delta / ullage) * 100, fillPct: tankFraction(h, t.diameter, t.capacity, t.ends) * 100 };
    })
    .sort(byDelta);
}

const nearestCapacity = (target) =>
  CAPACITIES.reduce((best, c) => (Math.abs(c - target) < Math.abs(best - target) ? c : best));

export function tanksFromMaxFill(maxFill, pct, ullage) {
  if (maxFill <= 0) return { capacity: 0, fuelGallons: 0, hits: [] };
  const capacity = nearestCapacity(maxFill / (pct / 100));
  const fuel = maxFill - ullage;
  const hits = UST_DATABASE.filter((t) => t.capacity === capacity).map((t) => {
    if (ullage < 0) return { ...t, fuelInches: null, over: false };
    if (fuel <= 0) return { ...t, fuelInches: 0, over: true };
    return { ...t, over: false, fuelInches: solveInchesFromVolume(Math.min(fuel, t.capacity), t.diameter, t.capacity, t.ends) };
  });
  return { capacity, fuelGallons: fuel, hits };
}

export function inchesFromTankVolume(gallons, tankSize, pct = 100) {
  if (tankSize <= 0) return { capacity: 0, hits: [] };
  const capacity = nearestCapacity(tankSize / (pct / 100));
  const hits = UST_DATABASE.filter((t) => t.capacity === capacity).map((t) => {
    if (gallons <= 0) return { ...t, fuelInches: 0, over: false };
    if (gallons >= t.capacity) return { ...t, fuelInches: t.diameter, over: true };
    return { ...t, over: false, fuelInches: solveInchesFromVolume(gallons, t.diameter, t.capacity, t.ends) };
  });
  return { capacity, hits };
}

export function fitFromReceipt(height, volume, ullage, kind) {
  if (height <= 0 || volume <= 0 || ullage < 0) return null;
  const full = Math.round(kind !== "90" ? volume + ullage : (volume + ullage) / 0.9);
  if (full <= volume) return null;
  const near = nearestCapacity(full);
  const catalogCapacity = Math.abs(near - full) / full <= 0.03 ? near : null;
  return {
    capacity: full, catalogCapacity,
    flatDiameter: solveDiameter(height, volume, full, "flat"),
    domeDiameter: solveDiameter(height, volume, full, "dome"),
    hits: catalogCapacity ? rankTanks(height, volume, String(catalogCapacity)) : [],
  };
}

export const SOLVER_MODES = ["stick", "level", "drop", "receipt", "ullage", "line", "volume"];

export const DEFAULT_SESSION = {
  capacity: 10000, diameter: 96, ends: "dome", startInches: 0, dropGallons: 0, product: "Regular 87",
  solverInches: 0, solverGallons: 0, solverCap: "ANY", solverMode: "stick", solverUllageTo: "full",
  solverFillLine: 0, solverFillPct: 90, solverTankGallons: 10000, solverTankPct: 100,
  solverStart: 0, solverEnd: 0, solverDelivered: 0,
  rcptHeight: 0, rcptVolume: 0, rcptUllage: 0, rcptUllageKind: "90",
}

// Old builds shipped sample numbers; clear any that were never changed so nobody reads them as real.
const OLD_SAMPLES = [{ startInches: 28.5, dropGallons: 3500 }, { solverInches: 44.25, solverGallons: 4800 }, { solverFillLine: 9000 },
  { solverStart: 28.5, solverEnd: 55.5, solverDelivered: 3500 }, { rcptHeight: 37.94, rcptVolume: 1881, rcptUllage: 3519 }];
const clearSamples = (s) => OLD_SAMPLES.reduce((o, g) => (Object.entries(g).every(([k, v]) => o[k] === v)
  ? { ...o, ...Object.fromEntries(Object.keys(g).map((k) => [k, 0])) } : o), s);;

const num = (v, fb, min, max) => (Number.isFinite(Number(v)) ? clamp(Number(v), min, max) : fb);
const pick = (v, allowed, fb) => (allowed.includes(v) ? v : fb);

// Cut by characters (not UTF-16 units) and drop broken surrogates so emoji never split.
export const clip = (str, n) => Array.from(String(str || "")).slice(0, n).join("").replace(/[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/g, "");

export function patchSession(s, patch) {
  const n = { ...s, ...patch };
  if (!("customDims" in patch) && ["capacity", "diameter", "ends"].some((k) => k in patch && patch[k] !== s[k])) { n.customDims = false; n.customLength = 0; }
  const r = (v, p) => Math.round(v * p) / p;
  return {
    ...n,
    capacity: num(n.capacity, 500, 500, 80000),
    diameter: num(n.diameter, 24, 24, 240),
    startInches: r(num(n.startInches, 0, 0, num(n.diameter, 24, 24, 240)), 1000),
    dropGallons: r(num(n.dropGallons, 0, 0, 40000), 10),
    solverInches: r(num(n.solverInches, 0, 0, 240), 1000),
    solverGallons: num(n.solverGallons, 0, 0, 80000),
    solverStart: r(num(n.solverStart, 0, 0, 240), 1000),
    solverEnd: r(num(n.solverEnd, 0, 0, 240), 1000),
    solverDelivered: r(num(n.solverDelivered, 0, 0, 40000), 10),
    solverFillLine: r(num(n.solverFillLine, 0, 0, 80000), 10),
    solverFillPct: pick(n.solverFillPct, [90, 95, 100], 90),
    solverTankGallons: r(num(n.solverTankGallons, 500, 500, 80000), 10),
    solverTankPct: pick(n.solverTankPct, [90, 95, 100], 100),
    solverMode: pick(n.solverMode, SOLVER_MODES, "drop"),
    ends: n.ends === "flat" ? "flat" : "dome",
    rcptHeight: r(num(n.rcptHeight, 0, 0, 240), 1000),
    rcptVolume: r(num(n.rcptVolume, 0, 0, 80000), 10),
    rcptUllage: r(num(n.rcptUllage, 0, 0, 80000), 10),
    rcptUllageKind: n.rcptUllageKind === "full" ? "full" : "90",
    solverUllageTo: n.solverUllageTo === "90" ? "90" : "full",
    solverCap: typeof n.solverCap === "string" ? n.solverCap : "ANY",
    product: PRODUCTS.some((p) => p.id === n.product) ? n.product : DEFAULT_SESSION.product,
    activeName: typeof n.activeName === "string" ? clip(n.activeName, 80) : undefined,
  };
}

export const normalizeSession = (s) => (s && typeof s === "object" ? patchSession(DEFAULT_SESSION, clearSamples(s)) : DEFAULT_SESSION);

// One way to write a tank everywhere: "Tank 1", never "T 1" or a bare "1".
export function tankLabel(raw) {
  const s = String(raw ?? "").trim();
  const m = s.match(/^(?:t(?:ank|k)?\.?\s*[-#]?\s*|#|no\.?\s*)?(\d+)(?:\s*[·:]\s*|\s+|$)(.*)$/i);
  if (!m || (!/^(t|#|no)/i.test(s) && !/^\d+(\s|[·:]|$)/.test(s))) return s;
  return m[2] ? `Tank ${m[1]}${s.includes("·") ? " ·" : ""} ${m[2]}` : `Tank ${m[1]}`;
}
export const siteTankName = (name) => {
  const parts = String(name ?? "").split(" · ");
  return parts.length > 1 ? [...parts.slice(0, -1), tankLabel(parts[parts.length - 1])].join(" · ") : String(name ?? "");
};
